// @avantia common-code
import { ensureDefined, getFromWindowRef, SMap } from '@avantia/client-and-server-utilities';
import { convertToValidator, HpValidator, setValidationExpressionThisReference } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { hasOwnProperty, HpError } from '@avantia/logging-and-errors';
import {
  copySection,
  createControlId,
  destructureControlId,
  getIdentifierConfigDefaultValues,
  HpAddControlGroupActionProps,
  HpAnswerMap,
  HpCommonSection,
  HpQuestion,
  HpQuestionData,
  HpQuestionLookupItemMap,
  HpQuestionMap,
  HpQuestionSet,
  HpSection,
  HpSectionMap,
  HpTokensAndExpressionsThisReference,
  identifierConfigChanged
} from '@avantia/questionset-model';
import { triggerNavigationChangedEvent } from '../actions/eventActions';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logError, logInfo, recordElapsedTimeOfFunc } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getCustomFunction, registerCustomFunctions } from '../customFunctionRegistry';
import { getPerQuestionSetConfig, HpPerQuestionSetConfig } from '../perQuestionSetConfigRegistry';
import { areSame } from '../tools/clientAndServerUtilities';
import { HpClientPrerequisiteThisReferenceClass } from '../tools/prerequisiteThisReference';
import { forEachSectionInOrder, setActiveSection } from '../tools/questionDisplayStateTools';
import { getQuestionIdFor } from '../tools/questionIds';
import { createVoidEvent } from '../tools/utilities';
import {
  HpClientAnswer,
  HpClientAnswerMap,
  HpClientAvailableQuestionSet,
  HpClientButtonData,
  HpClientDefaultAnswerExpressionFunction,
  HpClientDefaultAnswerExpressionFunctionProps,
  HpClientPrerequisiteThisReference,
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionMap,
  HpClientQuestionSetState,
  HpClientQuestionUiMap,
  HpClientSection,
  HpClientSectionMap,
  HpQuestionBasic,
  HpQuestionDisplayState,
  HpSectionBasic,
  showParticularSectionId
} from '../types';
import {
  createEmptyClientQuestionSetState,
  createEmptyDisplayState,
  createEmptyValidationResult
} from './initialState';
import { questionScriptResolver } from './questionScriptResolver';
import {
  addControlGroup,
  getVisibleQuestionCustomization,
  updateDefaultedAnswerDependencies
} from './questionSetReducerLibrary';
import {
  createBlankAnswer,
  createQuestionIdSorter,
  createSetupForTraverseRepeatingGroupTree,
  determineVisibleQuestions,
  evaluateQuestionPrerequisite,
  evaluateSectionPrerequisites,
  getHighestSectionAndErrorsIfApplicable,
  getTypeOfQuestion,
  traverseRepeatingGroupTree
} from './questionSetTools';
import { applyChanges } from './reducerLibrary';
import { sectionScriptResolver } from './sectionScriptResolver';

export interface HpSectionToQuestionsValue {
  isGroup: boolean;
  questionIds: string[];
}

export type HpSectionToQuestionMap = SMap<HpSectionToQuestionsValue>;

export interface HpQuestionSetPayloadMapperProps {
  bypassMoveToSectionCheck?: true; // most times, this should not be provided.
  currState: HpClientQuestionSetState;
  errorIfNoPayload: boolean;
  isValidControlType: HpIsValidControlTypeFunction;
  payload: HpQuestionSet | undefined;
}

interface HpWindowLocation {
  location: Location;
}

let clientDebugFlags: HpClientDebugFlags, websiteEndpoint: string, questionSetPagePath: string;
let { groupStartNumber, questionOrderPropertyIsString } = getIdentifierConfigDefaultValues(); // just a temp default, it will be set correctly below.

registerConfigurationAction((config) => {
  ({ clientDebugFlags, websiteEndpoint } = config);
  questionSetPagePath = `${websiteEndpoint}/`;
});

identifierConfigChanged(({ groupStartNumber: gsn, questionOrderPropertyIsString: qopis }) => {
  groupStartNumber = gsn;
  questionOrderPropertyIsString = qopis;
});

export function questionSetPayloadMapper(props: HpQuestionSetPayloadMapperProps): HpClientQuestionSetState | undefined {
  return recordElapsedTimeOfFunc('questionSetPayloadMapperFunction', questionSetPayloadMapperFunction, props);
}

function questionSetPayloadMapperFunction({
  currState,
  payload,
  errorIfNoPayload,
  bypassMoveToSectionCheck,
  isValidControlType
}: HpQuestionSetPayloadMapperProps): HpClientQuestionSetState | undefined {
  if (!payload) {
    if (errorIfNoPayload) {
      throw new Error('There must be a payload (or errorIfNoPayload should be set to false).');
    }

    return undefined;
  }

  const {
    productId,
    productDescription,
    answers: payloadAnswers = {},
    questions: payloadQuestions,
    stylesheets,
    questionFormats,
    friendlyName,
    headingControlType,
    validFrom,
    validTo,
    hashCode,
    questionSetId,
    sections,
    commonLookups,
    commonCustomData,
    commonHelpText,
    commonInfoText,
    commonWarningText,
    functionReferences,
    timestamp: qsetTimestamp,
    version: qsetVersion
  } = payload;

  const thisRef = new HpTokensAndExpressionsThisReference();
  setValidationExpressionThisReference(() => thisRef);

  if (functionReferences) {
    registerCustomFunctions(questionSetId, new Function('return (' + functionReferences + ')')());
  }

  questionScriptResolver({
    commonLookups,
    debugFlag: HpClientDebugFlags.PayloadMapper,
    funcRefResolver: (name) => getCustomFunction(questionSetId, name),
    productId,
    questions: payloadQuestions,
    sections
  });

  sectionScriptResolver({
    sections: payload.sections,
    funcRefResolver: (name) => getCustomFunction(questionSetId, name),
    debugMode: (clientDebugFlags & HpClientDebugFlags.PayloadMapper) > 0
  });

  lodash.forOwn(payloadQuestions, (q, id) => {
    convertPayloadQuestion(q, id);
  });

  lodash.forOwn(sections, (s, id) => {
    s.sectionId = id;
  });

  const displayState = createEmptyDisplayState({
    addBlankSection: false
  });

  const perQuestionSetConfig = getPerQuestionSetConfig({ productId });

  const { questions, answers, allOrderedQuestionIds, defaultedAnswersHaveBeenSet, timestamp, version } =
    linkSectionsAndPopulateDisplayState({
      productId,
      perQuestionSetConfig,
      commonLookups,
      questionSetId,
      payloadQuestions,
      sections,
      payloadAnswers,
      displayState,
      currState,
      isValidControlType
    });

  const state: HpClientQuestionSetState = {
    productId,
    productDescription,
    questionSetId,
    answersId: '',
    timestamp: timestamp || qsetTimestamp,
    validFrom,
    validTo,
    friendlyName,
    headingControlType,
    version: version || qsetVersion,
    hashCode,
    stylesheets,
    questionFormats,
    questions,
    answers,
    displayState,
    validationResult: createEmptyValidationResult(),
    commonLookups,
    commonCustomData,
    commonHelpText: commonHelpText || {},
    commonInfoText: commonInfoText || {},
    commonWarningText: commonWarningText || {}
  };

  return finaliseState({ state, allOrderedQuestionIds, defaultedAnswersHaveBeenSet, bypassMoveToSectionCheck });
}

export interface HpLinkSectionsAndPopulateDisplayStateProps {
  commonLookups: SMap<HpQuestionLookupItemMap>;
  currState: HpClientQuestionSetState;
  displayState: HpQuestionDisplayState;
  isValidControlType: HpIsValidControlTypeFunction;
  payloadAnswers: HpAnswerMap;
  payloadQuestions: HpQuestionMap;
  perQuestionSetConfig: HpPerQuestionSetConfig;
  productId: string;
  questionSetId: string;
  sections: HpSectionMap;
}

export function linkSectionsAndPopulateDisplayState({
  commonLookups,
  currState,
  displayState,
  isValidControlType,
  payloadAnswers,
  payloadQuestions,
  perQuestionSetConfig,
  productId,
  questionSetId,
  sections
}: HpLinkSectionsAndPopulateDisplayStateProps) {
  const links = processQuestionsAndLinkToSections({
    commonLookups,
    isValidControlType,
    payloadAnswers,
    payloadQuestions,
    perQuestionSetConfig,
    productId,
    sections
  });

  const { allOrderedQuestionIds, answers, defaultedAnswersHaveBeenSet, sectionToQuestionMap } = links;
  const questions = links.questions;
  const raiseNavigationChangedEvent = false;

  displayState.allOrderedQuestionIds = allOrderedQuestionIds;
  processSections(sections, sectionToQuestionMap, displayState, questions, raiseNavigationChangedEvent);

  [
    getQuestionIdFor(productId, 'activeSectionId'),
    getQuestionIdFor(productId, 'highestSectionStartedPosition')
  ].forEach((key) => {
    if (answers[key]) {
      displayState[key] = answers[key].customer;
    }
  });

  displayState.questionsUI = populateQuestionUiMap(allOrderedQuestionIds, questions, sections);
  const { friendlyName, productDescription, timestamp, version } = currState;
  displayState.availableQuestionSets = {
    selected: questionSetId,
    items: [
      {
        productId,
        questionSetId,
        productDescription,
        friendlyName,
        timestamp
      }
    ]
  };

  return { defaultedAnswersHaveBeenSet, allOrderedQuestionIds, questions, answers, timestamp, version };
}

export interface HpFinaliseStateProps {
  allOrderedQuestionIds: string[];
  bypassMoveToSectionCheck?: true; // most times, this should not be provided.
  defaultedAnswersHaveBeenSet: string[];
  state: HpClientQuestionSetState;
}

function finaliseState({
  allOrderedQuestionIds,
  bypassMoveToSectionCheck,
  defaultedAnswersHaveBeenSet,
  state
}: HpFinaliseStateProps): HpClientQuestionSetState {
  if (defaultedAnswersHaveBeenSet.length > 0) {
    state = updateDefaultedAnswerDependencies({
      state,
      questionIds: defaultedAnswersHaveBeenSet
    });
  }

  state = reloadQuestionGroupsFromAnswers(state);

  const { answers, displayState, productId, questions } = state;
  for (let i = 0, c = 0; i < allOrderedQuestionIds.length; i++) {
    const id = allOrderedQuestionIds[i];
    const question = questions[id];
    if (question !== undefined) {
      evaluateQuestionPrerequisite({
        question,
        eventType: 'none',
        state,
        questionsUI: displayState.questionsUI,
        index: c++
      });
    }
  }

  displayState.sectionsUI = evaluateSectionPrerequisites(displayState, state, 'none');

  const activeSection = answers[getQuestionIdFor(productId, 'activeSectionId')];
  const activeSectionId = activeSection ? (activeSection.customer as string) : undefined;
  const { highestSectionStartedPosition, highestSectionStartedId, validationResult } =
    getHighestSectionAndErrorsIfApplicable({
      state,
      displayState,
      bypassMoveToSectionCheck
    });

  if (validationResult) {
    state.validationResult = validationResult;
  }

  displayState.highestSectionStartedPosition = highestSectionStartedPosition;
  displayState.activeSectionId = ensureActiveSectionIsVisible(displayState, activeSectionId || highestSectionStartedId);

  displayState.visibleQuestionIds = determineVisibleQuestions({
    questionsUI: displayState.questionsUI,
    questions,
    activeSection: displayState.sections[displayState.activeSectionId],
    sections: displayState.sections,
    customization: getVisibleQuestionCustomization(productId)
  });

  state = addEmptyGroupToQuestionsWithNoGroups(state);
  if (!state.answers[showParticularSectionId]) {
    state.answers = applyChanges(state.answers, {
      [showParticularSectionId]: { customer: null, default: null }
    });
  }

  const { location } = getFromWindowRef<HpWindowLocation>(['location']) || {};
  if (location && location.pathname === questionSetPagePath) {
    const { displayState } = state;
    triggerNavigationChangedEvent({
      getEvent: createVoidEvent,
      activeSection: displayState.sections[displayState.activeSectionId],
      buttonAction: 'move-to-section'
    });
  }

  return state;
}

function ensureActiveSectionIsVisible(displayState: HpQuestionDisplayState, activeSectionId: string): string {
  const { sectionsUI } = displayState;
  if (sectionsUI[activeSectionId] && sectionsUI[activeSectionId].isVisible) {
    return activeSectionId;
  }

  const { sectionIdOrder } = displayState;
  const startIndex = sectionIdOrder.indexOf(activeSectionId) + 1;
  for (let i = startIndex; i < sectionIdOrder.length; i++) {
    const sectionId = sectionIdOrder[i];
    if (sectionsUI[sectionId].isVisible) {
      return sectionId;
    }
  }

  throw new Error(`No active Section ID could be determined.`);
}

function convertPayloadQuestion(q: HpQuestion, id: string): void {
  q.questionId = id; // Ensure that the question ID exists and matches the key
  const data = q.data as HpQuestionData;

  if (!data.validation && q.dataType !== 'button') {
    data.validation = [];
  }

  if (!data.lookup && !data.lookupExpr) {
    data.lookup = {};
  }

  if (lodash.isArray(data.dependentAnswers)) {
    if (data.dependentAnswers.length > 0) {
      throw new Error(`The dependentAnswers property is a map, not an array. QuestionID "${id}".`);
    } else {
      data.dependentAnswers = {}; // change arrays to maps
    }
  }
}

interface LinkQuestionsToSectionsResult {
  allOrderedQuestionIds: string[];
  answers: HpClientAnswerMap;
  defaultedAnswersHaveBeenSet: string[];
  questions: HpClientQuestionMap;
  sectionToQuestionMap: HpSectionToQuestionMap;
}

export interface HpProcessQuestionsAndLinkToSectionsProps {
  commonLookups: SMap<HpQuestionLookupItemMap>;
  isValidControlType: HpIsValidControlTypeFunction;
  payloadAnswers: HpAnswerMap;
  payloadQuestions: SMap<HpQuestion>;
  perQuestionSetConfig: HpPerQuestionSetConfig;
  productId: string;
  sections: HpSectionMap;
}

export function processQuestionsAndLinkToSections({
  commonLookups,
  isValidControlType,
  payloadAnswers,
  payloadQuestions,
  perQuestionSetConfig,
  productId,
  sections
}: HpProcessQuestionsAndLinkToSectionsProps): LinkQuestionsToSectionsResult {
  let questionPosition = 1;
  const sectionToQuestionMap: HpSectionToQuestionMap = {};
  const nameToQuestionIdMap: SMap<string> = {};
  const questions: HpClientQuestionMap = {};
  const answers: HpClientAnswerMap = {};
  const allQuestionIds: string[] = [];
  const defaultedAnswersHaveBeenSet: string[] = [];
  const displayState = createEmptyDisplayState({ addBlankSection: false });
  const thisRef = new HpClientPrerequisiteThisReferenceClass({
    answers: payloadAnswers,
    commonLookups,
    debugFlag: HpClientDebugFlags.PayloadMapper,
    displayState,
    groupNumber: undefined,
    indexes: undefined,
    productId,
    questions: payloadQuestions,
    sections,
    sourceQuestionId: '',
    sourceQuestionType: undefined
  });

  const treeSetup = createSetupForTraverseRepeatingGroupTree({
    sections,
    questions: payloadQuestions,
    answers: payloadAnswers
  });

  lodash.forOwn(payloadQuestions, (payloadQuestion: HpQuestion, questionId: string) => {
    const { question, defaultedAnswerHasBeenSet } = questionMapper(
      payloadQuestion,
      questionId,
      questionPosition++,
      sections,
      payloadAnswers,
      thisRef,
      isValidControlType
    );

    if (defaultedAnswerHasBeenSet) {
      defaultedAnswersHaveBeenSet.push(questionId);
    }

    processQuestionsAndLinkToSection(question, sectionToQuestionMap, allQuestionIds);
    questions[questionId] = question;
    nameToQuestionIdMap[question.name] = question.questionId;
    let isRepeatedAnswer = false;
    const { groupId } = question;
    if (groupId) {
      const { isRepeater } = sections[groupId];
      if (isRepeater) {
        isRepeatedAnswer = true;

        // To know where the group is in the repeating group tree.
        const updateAnswerAction = (answerId: string) => {
          const answer = payloadAnswers[answerId];
          if (answer) {
            answers[answerId] = answer;
          }
        };

        traverseRepeatingGroupTree({
          action: updateAnswerAction,
          sections,
          answers: payloadAnswers,
          questions: payloadQuestions,
          groupId,
          questionId,
          setup: treeSetup
        });
      }
    }

    if (!isRepeatedAnswer) {
      answers[questionId] = payloadAnswers[questionId];
    }

    logInfo(`Processed ${questionId} in processQuestionsAndLinkToSections function`, HpClientDebugFlags.PayloadMapper);
  });

  processPseudoSections(sections, nameToQuestionIdMap, sectionToQuestionMap);

  [
    getQuestionIdFor(productId, 'activeSectionId'),
    getQuestionIdFor(productId, 'highestSectionStartedPosition'),
    getQuestionIdFor(productId, 'customData')
  ].forEach((key) => {
    if (payloadAnswers[key]) {
      answers[key] = payloadAnswers[key];
    }
  });

  allQuestionIds.sort(createQuestionIdSorter(questions));

  if (perQuestionSetConfig.cleanAnswersOnLoad === false) {
    lodash.keys(payloadAnswers).forEach((key) => {
      if (!hasOwnProperty(answers, key)) {
        answers[key] = payloadAnswers[key];
      }
    });
  }

  return {
    questions,
    answers,
    allOrderedQuestionIds: allQuestionIds,
    sectionToQuestionMap,
    defaultedAnswersHaveBeenSet
  };
}

export function processPseudoSections(
  sections: HpSectionMap,
  nameToQuestionIdMap: SMap<string>,
  sectionToQuestionMap: HpSectionToQuestionMap
): void {
  const pseudoSections = lodash
    .keys(sections)
    .filter((sid) => sections[sid].questionIds)
    .map((sid) => sections[sid]);
  if (pseudoSections.length) {
    pseudoSections.forEach((s) => {
      const questionIds = (s.questionIds as string[]).map((id) => nameToQuestionIdMap[id] || id);
      s.questionIds = questionIds;
      const item = sectionToQuestionMap[s.sectionId];
      if (!item) {
        sectionToQuestionMap[s.sectionId] = { isGroup: false, questionIds };
      } else {
        item.questionIds = questionIds;
      }
    });
  }
}

export function processQuestionsAndLinkToSection(
  question: HpClientQuestion,
  map: HpSectionToQuestionMap,
  allQuestionIds: string[]
): void {
  const { groupId, questionId, sectionId } = question;
  if (sectionId) {
    if (!map[sectionId]) {
      map[sectionId] = { isGroup: false, questionIds: [questionId] };
    } else {
      map[sectionId].questionIds.push(questionId);
    }
  }

  if (groupId) {
    if (!map[groupId]) {
      map[groupId] = { isGroup: true, questionIds: [questionId] };
    } else {
      map[groupId].questionIds.push(questionId);
    }
  }

  allQuestionIds.push(questionId);
}

/**
 * All questions that are not in repeating groups are marked as visible
 */
function populateQuestionUiMap(
  allQuestionIds: string[],
  questions: HpClientQuestionMap,
  sections: SMap<HpCommonSection>,
  questionsUiMap?: HpClientQuestionUiMap
): HpClientQuestionUiMap {
  const uiMap: HpClientQuestionUiMap = questionsUiMap || {};
  allQuestionIds.forEach((questionId) => {
    const question = questions[questionId];
    const { groupId, visible } = question;
    const group = groupId ? sections[groupId] : undefined;
    const isVisible = visible !== false && (!group || group.isRepeater !== true);
    uiMap[questionId] = { isVisible, isEditing: false };
  });

  return uiMap;
}

export function changeQuestionSet(
  state: HpClientQuestionSetState,
  questionSetId: string,
  isValidControlType: HpIsValidControlTypeFunction
): HpClientQuestionSetState {
  const { answersId, commonLookups, displayState } = state;
  const { availableQuestionSets } = displayState;

  if (!availableQuestionSets.items) {
    return state;
  }

  const item: HpClientAvailableQuestionSet = availableQuestionSets.items.find(
    (i) => areSame(i.questionSetId, questionSetId) || areSame(i.productId, questionSetId)
  ) as HpClientAvailableQuestionSet;

  if (!item) {
    throw new Error(`There is no question set with id "${questionSetId}".`);
  }

  if (!item.questionSet) {
    throw new Error(`The question set with id "${questionSetId}" must have its "questionSet" property populated.`);
  }

  state = ensureDefined(
    questionSetPayloadMapperFunction({
      currState: createEmptyClientQuestionSetState(),
      payload: item.questionSet,
      errorIfNoPayload: true,
      isValidControlType
    }),
    'state'
  );

  const productId = item.productId;
  const perQuestionSetConfig = getPerQuestionSetConfig({ productId });

  const { allOrderedQuestionIds, defaultedAnswersHaveBeenSet } = linkSectionsAndPopulateDisplayState({
    productId,
    perQuestionSetConfig,
    questionSetId,
    payloadQuestions: state.questions as unknown as HpQuestionMap,
    sections: state.displayState.sections as unknown as HpSectionMap,
    payloadAnswers: state.answers,
    displayState: state.displayState,
    commonLookups,
    currState: state,
    isValidControlType
  });

  state = finaliseState({ state, allOrderedQuestionIds, defaultedAnswersHaveBeenSet });

  state.displayState = applyChanges(state.displayState, {
    availableQuestionSets: applyChanges(availableQuestionSets, { selected: questionSetId })
  });

  if (!state.answersId) {
    state.answersId = ensureDefined(answersId, 'answersId');
  }

  return state;
}

export interface HpQuestionMapperResult {
  defaultedAnswerHasBeenSet: boolean;
  question: HpClientQuestion;
}

export function reloadQuestionGroupsFromAnswers(state: HpClientQuestionSetState): HpClientQuestionSetState {
  const { answers, questions } = state;

  for (const answerId in answers) {
    const { questionId, groupNumber, indexes } = destructureControlId(answerId, { requirePrefix: false });
    const answer = answers[answerId] as HpClientAnswer;
    if (answer && answer.partial) {
      delete answer.partial; // TODO This shouldn't be here, but needs to be removed if present.
    }

    if (groupNumber !== undefined) {
      const groupId = questions[questionId].groupId;
      if (groupId && !state.questions[answerId]) {
        const sourceQuestionId = answerId;
        state = addControlGroup({
          groupId,
          sourceQuestionId,
          state,
          indexes,
          processPrereqsAndCalcs: false,
          groupNumber
        });
      }

      state.answers[answerId] = answer;
    }
  }

  return state;
}

export function addEmptyGroupToQuestionsWithNoGroups(state: HpClientQuestionSetState): HpClientQuestionSetState {
  const { answers, displayState, questions } = state;
  const { sections } = displayState;
  const controlGroupsToAdd = determineWhichGroupsNeedToBeAdded({ answers, questions, sections });
  let newState = state;
  for (const { groupId, sourceQuestionId } of controlGroupsToAdd) {
    newState = addControlGroup({ groupId, sourceQuestionId, state: newState });
  }

  return newState;
}

export interface HpDetermineWhichGroupsNeedToBeAddedProps {
  answers: SMap<HpClientAnswer>;
  questions: SMap<HpClientQuestion>;
  sections: HpClientSectionMap;
}

export interface HpControlGroupToAdd {
  groupId: string;
  sourceQuestionId: string;
}

/**
 * Iterates each question looking for 'dependentActions' that contain an 'add-control-group' action.
 *
 * For each matching question, if there are no groups defined, that questionId and groupId will be added to the result list.
 * @returns A list of groups that need to be added.
 */
export function determineWhichGroupsNeedToBeAdded({
  answers,
  questions,
  sections
}: HpDetermineWhichGroupsNeedToBeAddedProps): HpControlGroupToAdd[] {
  const controlGroupsToAdd: HpControlGroupToAdd[] = [];

  for (const questionId in questions) {
    const question = questions[questionId];
    const buttonData = question.data as HpClientButtonData;
    const { dependentActions } = buttonData;
    const answer = answers[questionId];
    const isAnswerTrue = answer && answer.customer === true;

    if (!dependentActions || !isAnswerTrue) {
      continue;
    }

    for (const dependentAction of dependentActions) {
      const props = dependentAction.props as HpAddControlGroupActionProps;
      if (!props || !answer) {
        break;
      }

      const isAddControlGroup = dependentAction.action === 'add-control-group';
      const { groupId } = props;
      if (!groupId) {
        if (isAddControlGroup) {
          logError(
            `The dependentAction for question ${questionId} has action "${dependentAction.action}" but groupId is "${groupId}".`
          );
        }

        continue;
      }

      const group = ensureDefined(sections[groupId], `sections[${groupId}]`);
      const { questionIds: groupQuestionIds = [] } = group;
      if (!isAddControlGroup || groupQuestionIds.length === 0) {
        continue;
      }

      // eslint-disable-next-line prefer-const
      let { groupNumber, indexes } = destructureControlId(questionId, { requirePrefix: false });
      if (groupNumber !== undefined) {
        if (!indexes) {
          indexes = [];
        }

        indexes.push(groupNumber);
      }

      const qId = createControlId({
        questionId: groupQuestionIds[0],
        groupNumber: groupStartNumber,
        indexes,
        options: { noPrefix: true }
      });

      if (!questions[qId]) {
        controlGroupsToAdd.push({ groupId, sourceQuestionId: questionId });
      }
    }
  }

  return controlGroupsToAdd;
}

export type HpIsValidControlTypeFunction = (controlType: string) => boolean;

function questionMapper(
  payloadQuestion: HpQuestion,
  questionId: string,
  questionPosition: number,
  sections: HpSectionMap,
  answers: HpAnswerMap,
  thisRef: HpClientPrerequisiteThisReference<HpQuestion>,
  isValidControlType: HpIsValidControlTypeFunction
): HpQuestionMapperResult {
  const question: HpClientQuestion = lodash.cloneDeep(payloadQuestion) as any;
  question.questionId = questionId;
  question.position = questionPosition;

  convertQuestionValidators(payloadQuestion, question);

  const { data } = question;
  const textAndStyle = data.textAndStyle;

  let defaultedAnswerHasBeenSet = false;
  if (!answers[questionId] || (data as HpClientQuestionData).dependentAnswers) {
    defaultedAnswerHasBeenSet = createAnswer({
      question: question as any,
      answers,

      // a check is made to ensure a question exists, and in this case alone we don't care, so this fake map keeps everything happy
      questions: { [questionId]: payloadQuestion },
      sections,
      thisRef
    });
  }

  // Ensure that controls are all valid.
  for (const style of [textAndStyle.standard, textAndStyle.fluent]) {
    if (!style) {
      continue;
    }

    const controlType = style.controlType;
    if (!controlType) {
      throw new Error(`There is no controlType for question ${questionId}.`);
    } else {
      const controlTypeString = `${controlType}Control`;
      if (!isValidControlType(controlTypeString)) {
        throw new Error(`The controlType "${controlType}" for question ${questionId} is invalid.`);
      }
    }
  }

  if (questionOrderPropertyIsString) {
    question.order = String(question.order);
  }

  return { question, defaultedAnswerHasBeenSet };
}

export interface HpCreateAnswerProps {
  answers: HpAnswerMap;
  options?: HpCreateAnswerOptions;
  question: HpQuestion | HpClientQuestion;
  questions: SMap<HpQuestionBasic>;
  sections: SMap<HpSectionBasic>;
  thisRef: HpClientPrerequisiteThisReference<HpQuestion>;
}

export interface HpCreateAnswerOptions {
  forRepeater: true;
}

export function createAnswer({
  answers,
  options,
  question,
  questions,
  sections,
  thisRef
}: HpCreateAnswerProps): boolean {
  const {
    dataType,
    default: defaultAnswer,
    defaultExpr: defaultExprFunc,
    groupId,
    isDefaultImplicit,
    questionId
  } = question;

  const { forRepeater } = options || {};
  const section = groupId ? sections[groupId] : undefined;
  if (isDefaultImplicit || dataType === 'button' || (section && section.isRepeater && forRepeater !== true)) {
    return false;
  }

  let defaultValue = defaultAnswer;
  let defaultedAnswerHasBeenSet = false;

  if (defaultExprFunc) {
    const self = thisRef as unknown as HpClientPrerequisiteThisReference<HpClientQuestion>;
    const { groupNumber, indexes, questionId: qid } = destructureControlId(questionId, { requirePrefix: false });
    const props: HpClientDefaultAnswerExpressionFunctionProps = {
      eventType: 'none',
      indexes,
      groupNumber,
      questionId: qid,
      questionType: getTypeOfQuestion(qid, questions, sections),
      sourceQuestionId: questionId
    };

    defaultValue = (defaultExprFunc as unknown as HpClientDefaultAnswerExpressionFunction).call(self, props);
  }

  if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
    if (!answers[questionId]) {
      answers[questionId] = createBlankAnswer(HpClientDebugFlags.DefaultAnswersAndDisabledExpressions);
    }

    answers[questionId].default = defaultValue;
    defaultedAnswerHasBeenSet = true;
    logInfo(() => {
      const defaultType = typeof defaultValue;
      return `The answer for question ${questionId} has default set to ${
        defaultType === 'boolean' || defaultType === 'number' ? defaultValue : `"${defaultValue}"`
      }.`;
    }, HpClientDebugFlags.DefaultAnswersAndDisabledExpressions);
  }

  return defaultedAnswerHasBeenSet;
}

function processSections(
  sections: HpSectionMap | HpClientSectionMap,
  sectionToQuestionMap: HpSectionToQuestionMap,
  displayState: HpQuestionDisplayState,
  questions: HpClientQuestionMap,
  raiseNavigationChangedEvent: boolean
): void {
  if (sections) {
    // This also filters out groups (which are special sections that aren't rendered as top-level elements).
    displayState.sections = {};

    for (const sectionId in sectionToQuestionMap) {
      const { isGroup, questionIds } = sectionToQuestionMap[sectionId];
      const section = sections[sectionId] as HpSection;
      if (!section) {
        const questionId = questionIds[0];
        const question = questions[questionId];
        throw new Error(
          `There is no section defined with id "${sectionId}" (used by question "${question.name}", "${questionId}").`
        );
      }

      const displaySection = copySection(section, sectionId) as unknown as HpClientSection;
      displayState.sections[sectionId] = displaySection;
      displayState.sectionsUI[sectionId] = { isVisible: true };
      if (isGroup) {
        displaySection.questionIds = questionIds;
      }
    }

    const sectionIdOrder: string[] = [];
    let activeId: string | undefined;
    forEachSectionInOrder({
      displayState,
      options: { iterate: 'sections-and-groups', ignoreOrderList: true },
      worker: ({ section, sectionId, isActive }) => {
        if (isActive) {
          activeId = sectionId;
        }

        if (!section.controlType && !section.isRepeater) {
          sectionIdOrder.push(sectionId);
        }
      }
    });

    displayState.sectionIdOrder = sectionIdOrder;
    setActiveSection(displayState, activeId || sectionIdOrder[0], undefined, raiseNavigationChangedEvent);
  } else {
    throw new Error(`This probably won't work unless there is at least one section!`);
  }
}

function convertQuestionValidators(question: HpQuestion, riskQuestion: HpClientQuestion): void {
  const data = question.data;
  const validators: HpValidator<unknown>[] = [];
  if (data.validation) {
    data.validation.forEach((validation) => {
      try {
        validators.push(convertToValidator(validation));
      } catch (ex) {
        let err = ex as HpError;
        if (!err.isHpError) {
          err = new HpError(ex);
        }

        logError(err);
      }
    });

    riskQuestion.data.validation = validators;
  }
}
